/**
 * OSNAP-OVERRIDE-MODES — the temporary Extended (Apparent) Intersection
 * override subsystem (ТЗ §8.6 "Обязательные временные override-режимы":
 * `Extended Intersection`, `Extended Apparent Intersection"; Autodesk "Object
 * Snaps" reference).
 *
 * # What this owns (the genuinely-new arm)
 *
 * The Shift-letter one-shot running override (base OSMODE ∪/∖ override + the
 * OSMODE suppress-all sentinel bit 16384) is already the single source of truth
 * in `selectEffectiveOsmode` (`dynamicInput/sysvarHotkeys.ts`), consumed by
 * `scene3d/scene/useOsnapBindings.ts`. This module deliberately does NOT
 * duplicate that effective-set logic — it adds ONLY the two DEFERRED
 * override-only modes that had no implementation: Extended Intersection
 * (`extint`) and Extended Apparent Intersection (`extappint`).
 *
 * # Model (AutoCAD-faithful)
 *
 * Both are OVERRIDE-ONLY (no OSMODE bit) and DEFERRED two-operand: the user
 * invokes the override, picks a FIRST object (no crossing under the cursor),
 * then moves toward a SECOND object; AutoCAD snaps to the point where the two
 * objects' geometry, EXTENDED along its natural path (segment → infinite line,
 * arc → full circle), would cross. `resolveExtendedOverride` computes that
 * crossing from the CANONICAL geometry + object graph (ТЗ §8.6 rule §545 — never
 * from pixels), reusing the same analytic `intersectShapes` primitives the
 * running INTERSECTION / APPARENT INTERSECTION pass uses (`bounded === false`).
 *
 * `extint` vs `extappint`: on the 2D drawing plane (Z = 0) the apparent
 * (view-projected) crossing coincides with the true extended crossing, so both
 * share the coplanar geometry here — they stay DISTINCT modes (glyph + label)
 * because the projection difference only appears under a 3D view, out of the
 * 2D-plane scope. This is a geometric identity on Z = 0, NOT a simplification
 * (both are fully computed).
 *
 * THREE-free / DOM-free — pure analytic geometry, deterministically testable
 * OUTSIDE the renderer layer (ТЗ §7).
 */

import {
  collectBoundedNearCursorShapes,
  entityCanProduceIntersectShape,
  intersectShapes,
  shapeDistanceToPoint,
  streamShapesForEntity,
  warnIntersectCapOnce,
  type IntersectShape,
  INTERSECT_PASS_ENTITY_CAP,
  INTERSECT_PASS_SHAPE_CAP,
} from "./osnapIntersections";
import type {
  ExtendedOverrideMode,
  OsnapCandidate,
  ResolveOsnapInput,
} from "./osnapTypes";
import type { RenderableEntity, Vec2 } from "../types";

/**
 * Examined-id WORK bound for the second-operand scan, DECOUPLED from the
 * eligible-operand SLOT cap (`INTERSECT_PASS_ENTITY_CAP`). A dense pickbox packed
 * with NON-intersectable ids (text / hatch / point / stale bboxes) must not let
 * the resolver `getEntityById` + kind-test EVERY id the spatial index returns —
 * only ids that pass `entityCanProduceIntersectShape` consume a SLOT, so the raw
 * scan is otherwise unbounded (@codex #126). Mirrors `gatherAppintEntities`'
 * `APPINT_BROAD_SCAN_CAP` work-vs-slot split: 8× the operand cap so a genuine
 * pickbox (a handful of operands) never trips it while a pathological id density
 * is bounded. Kept LOCAL (not imported from `resolveObjectSnap`, which imports
 * THIS module) so `temporaryOverride` stays an import-graph leaf.
 */
const SECOND_OPERAND_SCAN_CAP = INTERSECT_PASS_ENTITY_CAP * 8;

/** English SnapTip labels for the two override modes (parent layer localises). */
export const EXTENDED_OVERRIDE_LABEL: Record<ExtendedOverrideMode, string> = {
  extint: "Extended Intersection",
  extappint: "Extended Apparent Intersection",
};

/** Materialise an entity's straight/conic shapes, capped at `limit`. */
function collectShapesCapped(
  entity: RenderableEntity,
  limit: number,
): { shapes: IntersectShape[]; capped: boolean } {
  const shapes: IntersectShape[] = [];
  for (const s of streamShapesForEntity(entity)) {
    if (shapes.length >= limit) return { shapes, capped: true };
    shapes.push(s);
  }
  return { shapes, capped: false };
}

/**
 * Resolve the Extended (Apparent) Intersection override to a single snap
 * candidate, or `null`. Requires a CAPTURED first operand
 * (`input.extendedOverride.firstOwnerId` non-null) — the caller
 * (`resolveObjectSnap`) only delegates here in that state; while the first
 * operand is still un-picked the running snaps resolve normally so the user can
 * land the first pick.
 *
 * The FIRST operand is resolved by id (it may be arbitrarily far — you picked it
 * earlier, then moved away to hover the second). The SECOND operand is an eligible
 * object whose actual (bounded) geometry lies UNDER the cursor pickbox — AutoCAD
 * picks it by hovering its stroke, so a mere AABB overlap is NOT enough (@codex
 * #134). Their EXTENDED crossing (`bounded === false`) can land far from the
 * cursor — the override does NOT aperture-bound the crossing point (that is the
 * whole point of the deferred override), selecting the crossing NEAREST the cursor
 * when several qualify. `kind` carries the base glyph mode (`intersection` /
 * `appint`) so no new marker glyph is needed; `overrideMode` preserves the distinct
 * override identity for the SnapTip label; `ownerEntityId` reports the HOVERED
 * (second) operand so the hover highlight tracks the object under the cursor, not
 * the far first operand (@codex #142).
 */
export function resolveExtendedOverride(
  input: ResolveOsnapInput,
): OsnapCandidate | null {
  return resolveExtendedOverrideImpl(input).candidate;
}

/**
 * Work/cap diagnostics from one extended-override resolve — how much the
 * second-operand scan examined and which bounds tripped. Returned only by the
 * test-only seam {@link __resolveExtendedOverrideDiagForTest}; the production
 * {@link resolveExtendedOverride} projects just `candidate`. Lets the
 * deterministic resolver test (ТЗ §7) assert the perf bounds @codex #126 / #138
 * flag WITHOUT depending on `console.warn` capture.
 */
export interface ExtendedOverrideDiag {
  candidate: OsnapCandidate | null;
  /** ids pulled from the aperture spatial-index query and probed (finding #126). */
  examinedIds: number;
  /** ids that passed `entityCanProduceIntersectShape` (SLOT-consuming operands). */
  eligibleOperands: number;
  /** second-operand shapes fed into the pairwise sweep AFTER the exact-distance
   *  proximity gate — bounded by the GLOBAL `INTERSECT_PASS_SHAPE_CAP` (#138). */
  secondShapes: number;
  /** the examined-id WORK bound (`SECOND_OPERAND_SCAN_CAP`) was hit (#126). */
  idScanCapped: boolean;
  /** the eligible-operand SLOT cap (`INTERSECT_PASS_ENTITY_CAP`) was hit. */
  entityCapped: boolean;
  /** the GLOBAL second-operand shape budget was hit (#138). */
  shapeCapped: boolean;
  /** the second-operand shape SCAN backstop was hit. */
  scanCapped: boolean;
}

function resolveExtendedOverrideImpl(
  input: ResolveOsnapInput,
): ExtendedOverrideDiag {
  const empty: ExtendedOverrideDiag = {
    candidate: null,
    examinedIds: 0,
    eligibleOperands: 0,
    secondShapes: 0,
    idScanCapped: false,
    entityCapped: false,
    shapeCapped: false,
    scanCapped: false,
  };
  const ext = input.extendedOverride;
  if (!ext || ext.firstOwnerId === null) return empty;

  const first = input.getEntityById(ext.firstOwnerId);
  // Armed but the first operand is gone (deleted / VP-filtered / not
  // intersectable) → no extended snap (never falls back to running snaps: an
  // active override supersedes them for the pick).
  if (!first || !entityCanProduceIntersectShape(first)) return empty;

  const ppw = Math.max(input.pixelsPerWorld, 1e-12);
  const apertureWorld = input.aperturePx / ppw;
  const cursor = input.cursorWorld;

  // The second operand is the object under the cursor's aperture box. The
  // crossing itself is unbounded (extends both operands).
  const secondIds = input.spatialIndex.intersect({
    left: cursor.x - apertureWorld,
    top: cursor.y - apertureWorld,
    right: cursor.x + apertureWorld,
    bottom: cursor.y + apertureWorld,
  });

  // ── Bound the second-operand id scan (@codex #126) ─────────────────
  // Resolve ids to eligible operand ENTITIES, but cap the WORK (examined ids):
  // a dense pickbox of non-intersectable text/hatch/point ids must not resolve
  // every id just because only intersectable ids consume a SLOT. The SLOT cap
  // (`INTERSECT_PASS_ENTITY_CAP`) bounds eligible operands; the WORK cap
  // (`SECOND_OPERAND_SCAN_CAP`) bounds the raw scan, decoupled.
  const secondEntities: RenderableEntity[] = [];
  let examinedIds = 0;
  let idScanCapped = false;
  let entityCapped = false;
  for (const id of secondIds) {
    if (examinedIds >= SECOND_OPERAND_SCAN_CAP) {
      idScanCapped = true;
      break;
    }
    examinedIds += 1;
    if (id === first.id) continue; // never pair the first operand with itself
    const second = input.getEntityById(id);
    if (!second || !entityCanProduceIntersectShape(second)) continue;
    secondEntities.push(second);
    if (secondEntities.length >= INTERSECT_PASS_ENTITY_CAP) {
      entityCapped = true;
      break;
    }
  }

  // ── Gather the second operands' near-cursor shapes (@codex #138) ───
  // Reuse the SAME one-truth streaming gather the running INTERSECTION pass uses:
  // a GLOBAL shape budget (`INTERSECT_PASS_SHAPE_CAP`) + round-robin fair share +
  // scan backstop ACROSS all second operands — NOT a per-entity `SHAPE_CAP` reset
  // (the prior bug let a captured first polyline at the cap intersect
  // 4000×4000×operands second shapes and freeze dense drawings).
  const gathered = collectBoundedNearCursorShapes(
    secondEntities,
    cursor,
    apertureWorld,
  );

  // ── Require a real geometric hit under the pickbox (@codex #134) ───
  // The gather's per-shape prefilter is AABB-only; a long DIAGONAL segment's AABB
  // can straddle the cursor while the segment itself is far. AutoCAD requires the
  // SECOND object's actual geometry to be UNDER the pickbox — filter to shapes
  // whose EXACT distance to the cursor is within the aperture (computed from the
  // CANONICAL bounded geometry; the crossing stays unbounded via `bounded=false`).
  const nearSecond = gathered.shapes.filter(
    (s) => shapeDistanceToPoint(s.shape, cursor) <= apertureWorld,
  );

  const firstCollected = collectShapesCapped(first, INTERSECT_PASS_SHAPE_CAP);
  const firstShapes = firstCollected.shapes;
  const capped =
    firstCollected.capped ||
    gathered.shapeCapped ||
    gathered.scanCapped ||
    idScanCapped ||
    entityCapped;

  let bestWorld: Vec2 | null = null;
  // FINDING #142: the reported `ownerEntityId` is the HOVERED (second) operand —
  // `pointerSnapStep` swaps `hoverHitId` to this owner when magnet is on, so it
  // MUST be the object under the cursor, never the far already-picked first
  // operand. The deterministic selection tie-break is kept SEPARATE (below).
  let bestSecondOwner = "";
  // Deterministic, order-independent selection key for equal-distance crossings.
  // `first` is fixed across the whole resolve, so the second operand's id fully
  // disambiguates; the lexically-smaller wins (matches the running-pass rule) and
  // is INDEPENDENT of the spatial-index iteration order. Distinct from the
  // reported owner above so the tie-break can never force the far first operand.
  let bestTieKey = "";
  let bestDist = Number.POSITIVE_INFINITY;

  for (const a of firstShapes) {
    for (const s of nearSecond) {
      const tieKey = s.ownerId;
      for (const p of intersectShapes(a, s.shape, /* bounded */ false)) {
        const d = Math.hypot(p.x - cursor.x, p.y - cursor.y);
        // Nearest-to-cursor crossing wins; a distance tie breaks on the
        // lexically-smaller second-owner id (deterministic, order-independent).
        if (
          d < bestDist - 1e-12 ||
          (Math.abs(d - bestDist) <= 1e-12 &&
            (bestTieKey === "" || tieKey < bestTieKey))
        ) {
          bestDist = d;
          bestWorld = p;
          bestSecondOwner = s.ownerId;
          bestTieKey = tieKey;
        }
      }
    }
  }

  if (capped) {
    warnIntersectCapOnce(
      `[osnap] extended-intersection override capped at ${INTERSECT_PASS_ENTITY_CAP} operands / ${INTERSECT_PASS_SHAPE_CAP} shapes / ${SECOND_OPERAND_SCAN_CAP} probed ids; some extended crossings may be omitted`,
    );
  }

  const diag: ExtendedOverrideDiag = {
    candidate: null,
    examinedIds,
    eligibleOperands: secondEntities.length,
    secondShapes: nearSecond.length,
    idScanCapped,
    entityCapped,
    shapeCapped: gathered.shapeCapped,
    scanCapped: gathered.scanCapped,
  };
  if (!bestWorld) return diag;
  diag.candidate = {
    kind: ext.mode === "extint" ? "intersection" : "appint",
    overrideMode: ext.mode,
    world: bestWorld,
    ownerEntityId: bestSecondOwner,
    distancePxFromCursor: bestDist * ppw,
  };
  return diag;
}

/**
 * Test-only seam exposing the extended-override resolve WITH its work/cap
 * diagnostics (ТЗ §7 deterministic resolver tests). NOT used in production — the
 * hot path calls {@link resolveExtendedOverride}, which projects only the
 * candidate. Lets a test assert the second-operand scan bounds (@codex #126 /
 * #138) deterministically.
 */
export function __resolveExtendedOverrideDiagForTest(
  input: ResolveOsnapInput,
): ExtendedOverrideDiag {
  return resolveExtendedOverrideImpl(input);
}
